import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Badge,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import WorkExecutionAside from "./workExecutionAside";
import { formatCurrency } from "../../utils/Utils";
import { mockExecutionBatches } from "../../data/mockWorkExecution";

const { Option } = Select;
const { RangePicker } = DatePicker;

const WorkExecutionApprovals: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    stage: "",
    contractor: "",
    woNumber: "",
    dateRange: null as any,
    slaStatus: "",
  });

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = () => {
    setLoading(true);
    try {
      // Filter batches to show only those awaiting approval
      const pendingBatches = mockExecutionBatches.filter(
        (batch) =>
          batch.status === "Submitted" ||
          batch.status === "Checked" ||
          batch.status === "Verified"
      );
      setBatches(pendingBatches);
    } catch (error) {
      console.error("Error loading execution batches:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Submitted: "processing",
      Checked: "warning",
      Verified: "success",
      Approved: "success",
      Rejected: "error",
    };
    return statusColors[status] || "default";
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case "Submitted":
        return "📝";
      case "Checked":
        return "✅";
      case "Verified":
        return "🔍";
      default:
        return "📋";
    }
  };

  const handleOpenBatch = (record: any) => {
    navigate(
      `${process.env.PUBLIC_URL}/work-execution-work-order-details/${record.workOrderId}?mode=approval&batchId=${record.workExeBatchId}`
    );
  };

  const resetFilters = () => {
    setFilters({
      stage: "",
      contractor: "",
      woNumber: "",
      dateRange: null,
      slaStatus: "",
    });
  };

  const applyFilters = () => {
    setDrawerVisible(false);
    // Filter logic would be implemented here
  };

  // KPI calculations
  const submittedCount = batches.filter(
    (batch) => batch.status === "Submitted"
  ).length;
  const checkedCount = batches.filter(
    (batch) => batch.status === "Checked"
  ).length;
  const verifiedCount = batches.filter(
    (batch) => batch.status === "Verified"
  ).length;
  const slaBreachCount = batches.filter((batch) => batch.slaAge > 2).length;

  const columns: TableColumnsType<any> = [
    {
      title: "Batch #",
      dataIndex: "workBatchSequenceId",
      key: "workBatchSequenceId",
      width: 180,
      render: (text, record) => (
        <div>
          <Link
            to={`/work-execution-batch-details/${record.workExeBatchId}`}
            className="fw-bold text-primary"
          >
            {text}
          </Link>
          {/* <div className="text-muted small mt-1">Section: {record.section}</div> */}
        </div>
      ),
      // render: (text: string, record: any) => (
      //   <span
      //     className="inlineTableCellLink"
      //     id={`batchStatus-${record?.workExeBatchId}`}
      //     onClick={() => handleOpenBatch(record)}
      //   >
      //     {text}
      //   </span>
      // ),
    },
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 200,
      // render: (text, record) => (
      //   <div>
      //     <Link
      //       to={`/work-execution-batch-details/${record.workExeBatchId}`}
      //       className="fw-bold text-primary"
      //     >
      //       {text}
      //     </Link>
      //     {/* <div className="text-muted small mt-1">Section: {record.section}</div> */}
      //   </div>
      // ),
    },
    {
      title: "Site",
      dataIndex: "siteName",
      key: "siteName",
      width: 180,
    },
    // {
    //   title: "Stage",
    //   dataIndex: "stage",
    //   key: "stage",
    //   width: 120,
    //   render: (stage, record) => (
    //     <div>
    //       <Tag color={getStatusColor(record.status)}>
    //         {getStageIcon(stage)} {stage}
    //       </Tag>
    //     </div>
    //   ),
    // },
    {
      title: "Submitted On/By",
      key: "submitted",
      width: 160,
      render: (_, record) => (
        <div>
          <div className="fw-bold">
            {dayjs(record.submittedOn).format("DD-MMM-YYYY")}
          </div>
          <div className="text-muted small">{record.createdByName}</div>
        </div>
      ),
    },
    {
      title: "Last Action By",
      key: "lastAction",
      width: 160,
      render: (_, record) => (
        <div>
          {record.lastActionBy ? (
            <>
              <div className="fw-bold">{record.lastActionBy}</div>
              <div className="text-muted small">
                {dayjs(record.lastActionDate).format("DD-MMM HH:mm")}
              </div>
            </>
          ) : (
            <span className="text-muted">---</span>
          )}
        </div>
      ),
    },
    {
      title: "SLA Age",
      dataIndex: "slaAge",
      key: "slaAge",
      width: 100,
      align: "center",
      render: (days) => (
        <Badge
          count={`${days}d`}
          color={days > 2 ? "#ff4d4f" : days > 1 ? "#faad14" : "#52c41a"}
        />
      ),
      sorter: (a, b) => a.slaAge - b.slaAge,
    },
    {
      title: "Total Update Qty",
      dataIndex: "totalExecutedQuantity",
      key: "totalExecutedQuantity",
      width: 140,
      align: "right",
      render: (qty) => <strong>{qty?.toLocaleString()}</strong>,
    },
    // {
    //   title: "Flags",
    //   key: "flags",
    //   width: 100,
    //   render: (_, record) => (
    //     <div>
    //       {record.attachments && record.attachments.length > 0 && (
    //         <span title="Evidence Present">📎 </span>
    //       )}
    //       {record.slaAge > 2 && <span title="SLA Breach">🔶 </span>}
    //     </div>
    //   ),
    // },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   fixed: "right",
    //   width: 150,
    //   render: (_, record) => (
    //     <Space wrap>
    //       <Button
    //         type="primary"
    //         size="small"
    //         onClick={() => handleOpenBatch(record)}
    //       >
    //         Open
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="work-execution-pending-approvals"
      title="Pending Work Execution Approvals"
      columns={columns}
      dataSource={batches}
      loading={loading}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <WorkExecutionAside updateSm={setSm} sm={sm} />
        </div>
      }
      extra={{
        tableScrollX: 1600,
        showSearch: true,
        searchPlaceholder: "Search by Batch#, WO#, or Contractor...",
        kpiWidgets: [
          {
            title: "Submitted",
            value: submittedCount,
            icon: <ClockCircleOutlined />,
            color: "#1890ff",
          },
          {
            title: "Checked",
            value: checkedCount,
            icon: <CalendarOutlined />,
            color: "#faad14",
          },
          {
            title: "Verified",
            value: verifiedCount,
            icon: <UserOutlined />,
            color: "#52c41a",
          },
          {
            title: "SLA Breaches",
            value: slaBreachCount,
            icon: <DollarOutlined />,
            color: "#ff4d4f",
          },
        ],
        advancedFilter: {
          visible: drawerVisible,
          onClose: () => setDrawerVisible(false),
          onOpen: () => setDrawerVisible(true),
          content: (
            <div className="p-4">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <label className="form-label">Stage</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Stage"
                    value={filters.stage}
                    onChange={(value) =>
                      setFilters({ ...filters, stage: value })
                    }
                  >
                    <Option value="Submitted">Submitted</Option>
                    <Option value="Checked">Checked</Option>
                    <Option value="Verified">Verified</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Contractor</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Contractor"
                    value={filters.contractor}
                    onChange={(value) =>
                      setFilters({ ...filters, contractor: value })
                    }
                  >
                    <Option value="Galaxy Infra">Galaxy Infra</Option>
                    <Option value="PowerTech Electrical Services">
                      PowerTech Electrical Services
                    </Option>
                    <Option value="BuildRight Solutions Ltd">
                      BuildRight Solutions Ltd
                    </Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Work Order #</label>
                  <Input
                    placeholder="Enter WO Number"
                    value={filters.woNumber}
                    onChange={(e) =>
                      setFilters({ ...filters, woNumber: e.target.value })
                    }
                    allowClear
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Date Range</label>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={filters.dateRange}
                    onChange={(dates) =>
                      setFilters({ ...filters, dateRange: dates })
                    }
                    placeholder={["Submit From", "Submit To"]}
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">SLA Status</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select SLA Status"
                    value={filters.slaStatus}
                    onChange={(value) =>
                      setFilters({ ...filters, slaStatus: value })
                    }
                  >
                    <Option value="within">Within SLA</Option>
                    <Option value="breach">SLA Breach</Option>
                  </Select>
                </Col>
              </Row>
              <div className="mt-4 d-flex gap-2">
                <Button
                  type="primary"
                  onClick={applyFilters}
                  style={{ flex: 1 }}
                >
                  Apply Filters
                </Button>
                <Button onClick={resetFilters} style={{ flex: 1 }}>
                  Reset
                </Button>
              </div>
            </div>
          ),
        },
      }}
    />
  );
};

export default WorkExecutionApprovals;
